"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef, useState } from "react";

/**
 * Singleton IntersectionObserver manager for efficient lazy loading.
 * Creates a single observer instance shared across all components.
 */
class IntersectionObserverManager {
  private observer: IntersectionObserver | null = null;
  private callbacks = new WeakMap<Element, () => void>();
  private options: IntersectionObserverInit;
  constructor(options?: IntersectionObserverInit) {
    this.options = options || {
      rootMargin: "200px"
    };
  }
  getObserver(): IntersectionObserver {
    if (!this.observer) {
      this.observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const callback = this.callbacks.get(entry.target);
            if (callback) {
              callback();
              // Unobserve after triggering to avoid re-triggering
              this.observer?.unobserve(entry.target);
              this.callbacks.delete(entry.target);
            }
          }
        });
      }, this.options);
    }
    return this.observer;
  }
  observe(element: Element, callback: () => void): void {
    this.callbacks.set(element, callback);
    this.getObserver().observe(element);
  }
  unobserve(element: Element): void {
    this.getObserver().unobserve(element);
    this.callbacks.delete(element);
  }
  disconnect(): void {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
      this.callbacks = new WeakMap();
    }
  }
}

// Create singleton instance with default options
const defaultManager = new IntersectionObserverManager({
  rootMargin: "200px"
});

/**
 * Shared IntersectionObserver hook for efficient lazy loading.
 *
 * Uses a singleton IntersectionObserver instance that observes multiple elements,
 * avoiding the performance cost of creating one observer per image (e.g., 2019 observers).
 *
 * @param callback - Called when the element enters the viewport
 * @param options - Optional IntersectionObserver options (defaults to rootMargin: "200px")
 * @returns A ref callback to attach to the element
 *
 * @example
 * ```tsx
 * const imgRef = useIntersectionObserver(() => {
 *   setIsVisible(true);
 * });
 *
 * return <div ref={imgRef}>...</div>;
 * ```
 */
export function useIntersectionObserver(callback, options) {
  const $ = _c(11);
  const callbackRef = useRef(callback);
  const [element, setElement] = useState(null);
  const managerRef = useRef(null);
  let t0;
  let t1;
  if ($[0] !== callback) {
    t0 = () => {
      callbackRef.current = callback;
    };
    t1 = [callback];
    $[0] = callback;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  let t3;
  if ($[3] !== options) {
    t2 = () => {
      if (options) {
        managerRef.current = new IntersectionObserverManager(options);
      }
    };
    t3 = [options];
    $[3] = options;
    $[4] = t2;
    $[5] = t3;
  } else {
    t2 = $[4];
    t3 = $[5];
  }
  useEffect(t2, t3);
  let t4;
  if ($[6] !== element) {
    t4 = () => {
      if (!element) {
        return;
      }
      const manager = managerRef.current || defaultManager;
      manager.observe(element, () => callbackRef.current());
      return () => {
        manager.unobserve(element);
      };
    };
    $[6] = element;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== element || $[9] !== options) {
    t5 = [element, options];
    $[8] = element;
    $[9] = options;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  useEffect(t4, t5);
  return setElement;
}